/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-nested-ternary */
// @ts-nocheck
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { Loader } from '@components';
import { useAuth } from '@ui-modules/auth';
import { BOOKING_STATUS, ERRORS, malformedDataMessage } from '@common/constants';
import { Serializer, getErrors, getRecurringDriveErrorsText } from '@common/utils';
import {
  AcceptingDrive,
  AcceptingDriveData,
  AcceptingFormData,
  Drive,
  DrivePatch,
  FacilityDriver,
  FacilityVehicle,
  IBookingDetailsApi,
  IChoice,
  MappedDrive,
  PlannerFormData,
} from '@common/interfaces';
import { useFacility } from '@common/hooks';
import { AcceptBlock, BookingAccept, BookingPanel, MobilityPlanner } from './components';
import { RecurringForceCreate } from './components/MobilityPlanner/components';
import { sortBookingRoutes, Serializer as MobilitySerializer } from './utils';
import css from './styles.module.scss';

// Config
const SEARCH_FIELD = 'passenger_last_name';
const DATE_FORMAT_FULL = 'DD/MM/YYYY HH:mm:ss';

const mapDrivesForScheduler = (drives: Drive[]): MappedDrive[] =>
  drives.map((item: Drive) => ({
    ...item,
    booking: null,
    driver: null,
    id: item.uuid,
    end_date: moment(`${item.dropoff_date} ${item.dropoff_time}`, DATE_FORMAT_FULL).toDate(),
    is_hide: false,
    start_date: moment(`${item.pickup_date} ${item.pickup_time}`, DATE_FORMAT_FULL).toDate(),
  }));

const mapVehiclesForScheduler = (vehicles: FacilityVehicle[]): FacilityVehicle[] =>
  vehicles.map(({ vehicle_identifier, ...fields }) => ({
    ...fields,
    vehicle_identifier,
    key: vehicle_identifier.toString(),
  }));

const defaultDrive = {
  booking: 0, // TO DO: Should use null maybe
  driver: null,
  dropoff_date: '',
  dropoff_location: '',
  dropoff_time: '',
  dropoff_town: '',
  is_hide: true,
  is_temporary: true,
  editable_state: '',
  pickup_date: '',
  pickup_location: '',
  pickup_time: '',
  pickup_town: '',
  price_category: 'generic',
  vehicle: null,
  // TO DO: Should be delete
  start_date: new Date(),
  end_date: new Date(),
};

let savedAcceptingData: any;

const MobilityPlannerPage: FC = () => {
  const {
    agencyName,
    facility: { agencyId, city, country, facilityId },
  } = useFacility();
  const { t } = useTranslation();

  const { axios } = useAuth();

  const navigate = useNavigate();
  const urlParams = useParams<{ id: string; driveId?: string }>();
  const bookingId = urlParams.id;
  const driveId = urlParams?.driveId;

  const isEditDriveProcess = !!driveId;
  const isAcceptingProcess = !!bookingId && !isEditDriveProcess;

  const [accepted, setAccepted] = useState<boolean>(false);
  const [acceptingSecond, setAcceptingSecond] = useState<boolean>(false);

  const [vehicles, setVehicles] = useState<Array<FacilityVehicle>>([]);
  const [drivers, setDrivers] = useState<Array<FacilityDriver>>([]);
  const [drives, setDrives] = useState<MappedDrive[]>([]);

  const [bookingData, setBookingData] = useState<IBookingDetailsApi>();
  const [acceptingDrive, setAcceptingDrive] = useState<AcceptingDrive>(defaultDrive);
  const [acceptingSecondDrive, setAcceptingSecondDrive] = useState<AcceptingDrive>(defaultDrive);
  const [acceptingDriveData, setAcceptingDriveData] = useState<AcceptingDriveData[]>([]);

  const [showForceCreateRecurring, setShowForceCreateRecurring] = useState<boolean>(false);
  const [createRecurringErrors, setCreateRecurringErrors] = useState<string>();

  const [purposes, setPurposes] = useState<IChoice[]>([]);
  const [requestingUnits, setRequestingUnits] = useState<IChoice[]>([]);

  // TO DO: Refactor loading after merging
  const [load, setLoad] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const [currentDate, setCurrentDate] = useState<string>();

  const activeVehicles = useMemo(
    () => vehicles.filter((vehicle) => !vehicle.is_disabled),
    [vehicles],
  );

  const isMultiLeg = bookingData?.type_of_trip === 'multi-leg';

  const createDrives = (booking: IBookingDetailsApi): void => {
    const {
      agency_fk,
      routes,
      booking_agent_first_name,
      booking_agent_last_name,
      drives,
      email,
      first_name,
      uuid,
      last_name,
      n_of_passengers,
      phone_number,
      remarks,
      state,
    } = booking;

    sortBookingRoutes(routes);

    if (state !== BOOKING_STATUS.NOT_PROCESSED && !isEditDriveProcess) {
      toast.warning(t('messageBookingAccepted'));
      return;
    }

    if (!routes.length) {
      toast.warning(malformedDataMessage, { toastId: 'marlformed-data' });

      const currentDate = new Date(new Date().setMinutes(0));

      setAcceptingDrive((prevDrive) => ({
        ...prevDrive,
        booking: uuid,
        pickup_time: currentDate,
        pickup_date: currentDate,
        invalid: true,
      }));

      setCurrentDate(moment().format('YYYY-MM-DD'));

      return;
    }

    if (routes && routes[0]) {
      const {
        dropoff_date,
        dropoff_location,
        dropoff_time,
        dropoff_town,
        pickup_date,
        pickup_location,
        pickup_time,
        pickup_town,
        booked_route_order,
      } = routes[0];

      const isProceed = drives && drives.length !== 0;

      if (isEditDriveProcess) {
        const selectedDrive = routes.find((route) => route.drive_id === driveId);
        setCurrentDate(moment(selectedDrive?.pickup_date).format('YYYY-MM-DD'));
      } else {
        setCurrentDate(moment(pickup_date).format('YYYY-MM-DD'));
      }

      setAcceptingDrive((prevDrive) => ({
        ...prevDrive,
        booking: uuid,
        dropoff_date,
        dropoff_location,
        // TO DO: Should be refactored
        dropoff_time: null,
        dropoff_town,
        pickup_date,
        pickup_location,
        pickup_time,
        pickup_town,
        booked_route_order,

        driver_agency: null,
        driver_email: null,
        driver_first_name: null,
        driver_id: isProceed && drives ? drives[0].driver.id : null, // TO DO: Check this one
        driver_last_name: null,
        driver_phone_number: null,
        passenger_agency: agency_fk?.complete_name,
        passenger_email: email,
        passenger_first_name: first_name,
        passenger_last_name: last_name,
        passenger_number_of_pax: n_of_passengers,
        passenger_phone_number: phone_number,
        passenger_remarks: remarks,
        focal_point_first_name: booking_agent_first_name,
        focal_point_last_name: booking_agent_last_name,

        vehicle: isProceed && drives ? drives[0].vehicle.id : null,
        id: isProceed && drives ? drives[0].id : undefined,

        // TO DO: Should be delete
        start_date: moment(`${pickup_date} ${pickup_time}`, DATE_FORMAT_FULL).toDate(),
        end_date: moment(`${dropoff_date} ${dropoff_time}`, DATE_FORMAT_FULL).toDate(),
      }));
    }

    // Note: If Round Trip...
    if (routes && routes[1]) {
      const {
        dropoff_date,
        dropoff_location,
        dropoff_time,
        dropoff_town,
        pickup_date,
        pickup_location,
        pickup_time,
        pickup_town,
        booked_route_order,
      } = routes[1];

      setAcceptingSecondDrive((prevDrive) => ({
        ...prevDrive,
        booking: uuid,
        dropoff_date,
        dropoff_location,
        dropoff_time: null,
        dropoff_town,
        pickup_date,
        pickup_location,
        pickup_time,
        pickup_town,
        vehicle: null,
        booked_route_order,

        driver_agency: null,
        driver_email: null,
        driver_first_name: null,
        driver_id: null, // TO DO: Check this one
        driver_last_name: null,
        driver_phone_number: null,
        passenger_agency: agency_fk?.complete_name,
        passenger_email: email,
        passenger_first_name: first_name,
        passenger_last_name: last_name,
        passenger_number_of_pax: n_of_passengers,
        passenger_phone_number: phone_number,
        passenger_remarks: remarks,
        focal_point_first_name: booking_agent_first_name,
        focal_point_last_name: booking_agent_last_name,

        // TO DO: Should be delete (or correct)
        start_date: moment(`${pickup_date} ${pickup_time}`, DATE_FORMAT_FULL).toDate(),
        end_date: moment(`${dropoff_date} ${dropoff_time}`, DATE_FORMAT_FULL).toDate(),
      }));
    }
  };

  const createDrivesData = (booking: IBookingDetailsApi): void => {
    const {
      agency_fk,
      routes,
      booking_agent_first_name,
      booking_agent_last_name,
      drives,
      email,
      first_name,
      uuid,
      last_name,
      n_of_passengers,
      phone_number,
      remarks,
    } = booking;

    if (!routes.length) {
      toast.warning(malformedDataMessage, { toastId: 'marlformed-data' });

      const currentDate = new Date(new Date().setMinutes(0));

      setAcceptingDriveData((prev) => [
        ...prev,
        {
          booking: uuid,
          pickup_time: currentDate,
          pickup_date: currentDate,
          dropoff_date: currentDate,
          dropoff_time: currentDate,
          start_date: new Date(),
          end_date: new Date(),
          is_hide: true,
          invalid: true,
        },
      ]);

      setCurrentDate(moment().format('YYYY-MM-DD'));

      return;
    }

    if (routes.length) {
      routes.forEach((route, idx) => {
        const {
          dropoff_date,
          dropoff_location,
          dropoff_time,
          dropoff_town,
          pickup_date,
          pickup_location,
          pickup_time,
          pickup_town,
          booked_route_order,
        } = route;

        if (!currentDate && !idx) {
          let date = pickup_date;

          if (isEditDriveProcess) {
            const selectedDrive = drives.find((drive) => drive.uuid === driveId);
            date = selectedDrive?.pickup_date;
          }

          setCurrentDate(moment(date).format('YYYY-MM-DD'));
        }

        const isProceed = drives && drives.length !== 0;

        setAcceptingDriveData((prev: AcceptingDriveData[]) => [
          ...prev,
          {
            accepted: false,
            booking: uuid,
            driver: isProceed && drives ? drives[0].driver.uuid : null,
            driver_agency: null,
            driver_email: null,
            driver_first_name: null,
            driver_id: isProceed && drives ? drives[0].driver.uuid : null,
            driver_last_name: null,
            driver_phone_number: null,
            dropoff_date,
            dropoff_location,
            dropoff_time: null,
            dropoff_town,
            editable_state: '',
            end_date: moment(`${dropoff_date} ${dropoff_time}`, DATE_FORMAT_FULL).toDate(),
            focal_point_first_name: booking_agent_first_name,
            focal_point_last_name: booking_agent_last_name,
            id: isProceed && drives ? drives[0].id : undefined,
            is_hide: true,
            is_temporary: true,
            passenger_agency: agency_fk?.complete_name,
            passenger_email: email,
            passenger_first_name: first_name,
            passenger_last_name: last_name,
            passenger_number_of_pax: n_of_passengers,
            passenger_phone_number: phone_number,
            passenger_remarks: remarks,
            pickup_date,
            pickup_location,
            pickup_time,
            pickup_town,
            start_date: moment(`${pickup_date} ${pickup_time}`, DATE_FORMAT_FULL).toDate(),
            vehicle: isProceed && drives ? drives[0].vehicle.id : null,
            vehicle_id: isProceed && drives ? drives[0].vehicle.id : null,
            booked_route_order,
          },
        ]);
      });
    }
  };

  const getDriveSchema = useCallback(async (): Promise<void> => {
    try {
      const response = await axios.get(`/facilities/${facilityId}/planner/booking-form/`);

      const result = response?.data;
      if (result) {
        setPurposes(result?.booking_extra?.children?.purpose?.choices);
        setRequestingUnits(result?.booking_extra?.children?.requesting_unit?.choices);
      }
    } catch (error) {
      console.log(error);
    }
  }, [facilityId]);

  const getVehicles = async (date: string): Promise<void> => {
    const query = new URLSearchParams({
      agency_id: agencyId.toString(),
      date,
    });

    try {
      const response = await axios.get<FacilityVehicle[]>(
        `/facilities/${facilityId}/planner/vehicles/?${query}`,
      );
      const results = response?.data;

      if (results) {
        setVehicles(mapVehiclesForScheduler(results));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getDrivers = async (date: string): Promise<void> => {
    const query = new URLSearchParams({
      agency_id: agencyId.toString(),
      date,
    });

    try {
      const response = await axios.get(`/facilities/${facilityId}/planner/drivers/?${query}`);
      const results = response?.data;
      if (results) {
        setDrivers(results);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getDrives = async (date: string): Promise<void> => {
    const query = new URLSearchParams({
      agency_id: agencyId?.toString(),
      date,
      is_temporary: 'false',
    });

    try {
      const response = await axios.get(`/facilities/${facilityId}/planner/drives/?${query}`);
      const results = response?.data;

      if (results) {
        setDrives(mapDrivesForScheduler(results));
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getCurrentDrives = (): void => {
    if (currentDate) {
      getDrives(currentDate);
    }
  };

  const getBooking = async (): Promise<void> => {
    try {
      const response = await axios.get(`/facilities/${facilityId}/bookings/${bookingId}/`);
      const result = response?.data;

      if (result) {
        setBookingData(result);
        createDrives(result);
        createDrivesData(result);
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(getErrors(error.response.data));
      }
    }
  };

  const rejectBooking = async (reason: string): Promise<void> => {
    const query = new URLSearchParams({
      agency: agencyId?.toString(),
    });

    try {
      const response = await axios.patch(
        `/facilities/${facilityId}/bookings/${bookingId}/?${query}`,
        {
          state: 'rejected',
          reject_comment: reason,
        },
      );

      const result = response?.data;

      if (result) {
        toast.warning(t('booking.messageBookingRejected'));
        navigate('/bookings');
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(getErrors(error.response.data));
      }
    }
  };

  const postDrive = async (data: DrivePatch, driveType?: string): Promise<any> => {
    setLoad(true);

    try {
      const response = await axios.post(
        `/facilities/${facilityId}/planner/booking-form/existed/${data.booking}/`,
        { agency_id: agencyId, ...data },
      );

      const result = response?.data;

      if (result) {
        // Default Logic
        if (!driveType) {
          const newItem = mapDrivesForScheduler([result])[0];
          setDrives((prevDrives) => [...prevDrives, newItem]);
          setLoad(false);
          return result;
        }

        // Accepting Logic
        switch (driveType) {
          case 'accept-one-way-trip':
            toast.success(t('mobility.messageDrivePublished'));
            break;
          case 'accept-round-trip':
            toast.success(t('mobility.messageDrivesPublished'));
            break;
          default:
            console.log('Error driveType');
        }

        navigate('/bookings');
      }
    } catch (e: any) {
      setLoad(false);

      if (e.response.data?.errors?.length && bookingData?.recurring) {
        setCreateRecurringErrors(getRecurringDriveErrorsText(e));
        setShowForceCreateRecurring(true);
        savedAcceptingData = data;
        savedAcceptingData.driveType = driveType;
      } else {
        toast.error(getErrors(e.response.data));
      }

      return 'error';
    }

    setLoad(false);
  };

  const updateDrive = async (data: DrivePatch, skipUpdatingDrives = false): Promise<void> => {
    try {
      const response = await axios.patch(`/facilities/${facilityId}/planner/drives/${data.uuid}/`, {
        agency_id: agencyId,
        ...data,
      });

      const result = response?.data;

      if (result) {
        if (!skipUpdatingDrives) {
          setDrives((prevDrives) => {
            // TO DO: Use mergeArrayState

            const newArray = [...prevDrives];
            const index = newArray.findIndex((drive) => drive.id === result.id);
            // eslint-disable-next-line prefer-destructuring
            newArray[index] = mapDrivesForScheduler([result])[0];
            return newArray;
          });
          // TO DO: Map this results
          if (currentDate) {
            getDrives(currentDate);
          }
        }

        toast.success(t('mobility.messageUpdateDrive'));

        if (isEditDriveProcess) {
          setTimeout(() => navigate(`/bookings/${bookingId}`), 1000);
        }
      }
    } catch (error: any) {
      if (error.response) {
        toast.error(getErrors(error.response.data));
      }
      console.log(error);
    }
  };

  const postAcceptingForm = async (data: AcceptingFormData): Promise<void> => {
    setLoad(true);

    try {
      const { status } = await axios.post(
        `/facilities/${facilityId}/planner/booking-form/existed/${bookingData?.uuid}/`,
        data,
      );

      if (status === 201) {
        toast.success(t('mobility.messageDrivePublished'));
        navigate('/bookings');
      }
    } catch (e: any) {
      if (e.response.data?.errors?.length && bookingData?.recurring) {
        setCreateRecurringErrors(getRecurringDriveErrorsText(e));
        setShowForceCreateRecurring(true);
        savedAcceptingData = data;
      } else {
        toast.error(getErrors(e.response.data));
      }
    }

    setLoad(false);
  };

  const postDriversForm = async (formData: PlannerFormData): Promise<void> => {
    setLoad(true);

    try {
      await axios.post(`/facilities/${facilityId}/planner/booking-form/`, formData);
    } catch (e: any) {
      if (e.response.data?.errors?.length && formData.recurring_option) {
        throw new Error(ERRORS.RECURRING_CONFLICTING, {
          cause: getRecurringDriveErrorsText(e),
        });
      } else {
        toast.error(getErrors(e.response.data));
      }
    } finally {
      setLoad(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    Promise.allSettled([getDriveSchema()]).then(() => {
      setLoading(false);
    });
  }, [getDriveSchema]);

  useEffect(() => {
    if (!currentDate && !bookingId) {
      setCurrentDate(moment().format('YYYY-MM-DD'));
    }

    if (currentDate) {
      const isValidDate = moment(currentDate, 'YYYY-MM-DD').isValid();

      if (!isValidDate) {
        toast.warning(t('mobility.messageDateError'));
        return;
      }

      if (agencyId) {
        setLoading(true);
        Promise.allSettled([
          getDrivers(currentDate),
          getDrives(currentDate),
          getVehicles(currentDate),
        ]).then(() => {
          setLoading(false);
        });
      }
    }
  }, [currentDate, agencyId]);

  useEffect(() => {
    if (bookingId) {
      getBooking();
    }
  }, [bookingId]);

  const totalDrives = [...drives];

  if (acceptingDrive && acceptingDrive.booking) {
    totalDrives.push(acceptingDrive);
  }

  if (acceptingSecondDrive && acceptingSecondDrive.booking) {
    totalDrives.push(acceptingSecondDrive);
  }

  if (isMultiLeg && acceptingDriveData) {
    totalDrives.push(
      ...acceptingDriveData.map((drive) => ({
        ...drive,
        vehicle_agency: 0,
        is_hide: drive.is_hide ?? false,
        driver_agency: drive.driver_agency ?? '',
        driver_email: drive.driver_email ?? '',
        driver_first_name: drive.driver_first_name ?? '',
        driver_last_name: drive.driver_last_name ?? '',
        driver_id: drive.driver_id ?? 0,
        driver_phone_number: drive.driver_phone_number ?? '',
        editable_state: drive.editable_state ?? '',
        passenger_agency: drive.passenger_agency ?? '',
        vehicle_id: drive.vehicle_id ?? 0,
      })),
    );
  }

  useEffect(() => {
    if (isMultiLeg && !acceptingDriveData?.[0]?.invalid) {
      const acceptingCompleted =
        acceptingDriveData.filter(({ accepted }) => accepted).length === bookingData.routes.length;

      if (acceptingCompleted) {
        postAcceptingForm(MobilitySerializer.mapAcceptingFormToPostData(acceptingDriveData));
      }
    }

    if (isMultiLeg && acceptingDriveData) {
      const currentDrive = acceptingDriveData.find((drive) => !drive.accepted);

      setCurrentDate(moment(currentDrive?.pickup_date!).format('YYYY-MM-DD'));
    }
  }, [acceptingDriveData]);

  return (
    <div className={css.container}>
      <div className={css.panel}>
        <div className={css.title}>
          {city} | {country}
          <span className={css.bold}> {agencyName}</span>
        </div>
      </div>

      {bookingData && isEditDriveProcess && !loading && (
        <BookingPanel
          bookingData={Serializer.formatBookingDetails(bookingData)}
          drivers={drivers.map(Serializer.formatFacilityDriver)}
          isEditDriveProcess
          vehicles={activeVehicles.map(Serializer.formatFacilityVehicle)}
          onDriveCancel={() => {
            getBooking();
            getCurrentDrives();
          }}
        />
      )}

      {acceptingDrive.booking !== 0 &&
        bookingData &&
        !isMultiLeg &&
        !loading &&
        !isEditDriveProcess && (
          <AcceptBlock
            accepted={accepted}
            acceptingSecond={acceptingSecond}
            acceptingDrive={acceptingDrive}
            acceptingSecondDrive={acceptingSecondDrive}
            bookingData={bookingData}
            drivers={drivers}
            totalDrives={totalDrives}
            vehicles={activeVehicles}
            postDrive={postDrive}
            rejectBooking={rejectBooking}
            setAccepted={setAccepted}
            setAcceptingDrive={setAcceptingDrive}
            setAcceptingSecond={setAcceptingSecond}
            setAcceptingSecondDrive={setAcceptingSecondDrive}
            setCurrentDate={setCurrentDate}
            updateDrive={updateDrive}
          />
        )}

      {!loading && bookingData && isMultiLeg && !isEditDriveProcess && (
        <BookingAccept
          acceptingDriveData={acceptingDriveData}
          bookingData={bookingData}
          drivers={drivers}
          totalDrives={totalDrives}
          vehicles={activeVehicles}
          rejectBooking={rejectBooking}
          setAcceptingDriveData={setAcceptingDriveData}
          setCurrentDate={setCurrentDate}
        />
      )}

      <div className={css.content}>
        {load ? <Loader fullScreen /> : null}
        {loading ? (
          <div className={css.wrapLoader}>
            <Loader />
          </div>
        ) : vehicles.length === 0 ? (
          <div className={css.empty}>No vehicles</div>
        ) : drivers.length === 0 ? (
          <div className={css.empty}>No drivers</div>
        ) : (
          <MobilityPlanner
            agencyId={agencyId}
            agencyName={agencyName}
            city={city}
            currentDate={currentDate}
            drivers={drivers}
            drives={totalDrives}
            isAcceptingProcess={isAcceptingProcess}
            locationId={facilityId}
            postDriversForm={postDriversForm}
            purposes={purposes}
            requestingUnits={requestingUnits}
            searchField={SEARCH_FIELD}
            vehicles={activeVehicles}
            getCurrentDrives={getCurrentDrives}
            onViewChange={(_new_mode, new_date) => {
              setCurrentDate(moment(new_date).format('YYYY-MM-DD'));
            }}
            updateDrive={updateDrive}
          />
        )}
        {showForceCreateRecurring ? (
          <RecurringForceCreate
            errors={createRecurringErrors}
            onCancelClick={() => setShowForceCreateRecurring(false)}
            onConfirmClick={() => {
              setShowForceCreateRecurring(false);

              if (isMultiLeg) {
                postAcceptingForm({ ...savedAcceptingData, force_create: true });
              } else {
                postDrive(
                  { ...savedAcceptingData, force_create: true },
                  savedAcceptingData.driveType,
                  true,
                );
              }
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

export default MobilityPlannerPage;
